import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { showToast } from '@/features/toast/toastSlice';
import {
	buildUnlayerUserContext,
	getEmptyUnlayerDesign,
} from '@/components/Drawers/AddUnlayerEmailDrawer/helpers';
import {
	findAvailableSignatureEmailOption,
	getAvailableSignatureEmailOptions,
} from '@/features/unlayerUserSignatures/helpers';
import {
	isSignatureHtmlEmpty,
	resolveSignatureSnapshotHtml,
} from '@/features/unlayerUserSignatures/html';
import {
	useGetUnlayerUserSignaturesQuery,
	useUpsertUnlayerUserSignatureMutation,
} from '@/features/unlayerUserSignatures/unlayerUserSignaturesApi';
import { logAuditActions } from '@/features/auditActions/auditActions';
import { getUnlayerTagData, getUnlayerTags } from '@/utils/unlayer';

import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { cloneDesign, getDefaultProfileSignatureEmailOption, getUnlayerExport } from './helpers';

/**
 * Coordinates the stateful profile signature authoring workflow: per-email
 * loading, invalid-design recovery, merge-tag context, and save/export logic.
 * Pass userId (the target user's Firestore doc id) to author signatures for
 * another broker-group user; otherwise this authors for the logged-in user.
 */
export const useProfileSignatureAuthoring = (userId) => {
	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const hasTarget = userId != null;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { data: profileUserDetails } = useGetUserDetailsQuery(
		{ userId },
		{
			skip: !hasTarget,
		},
	);

	const signatureOwner = hasTarget ? profileUserDetails : userDetails;

	const editorRef = useRef(null);
	const hasInitialEmailSelectionRef = useRef(false);
	const [selectedEmailOption, setSelectedEmailOption] = useState(null);
	const [designToLoad, setDesignToLoad] = useState(null);
	const [loadError, setLoadError] = useState('');
	const [saveError, setSaveError] = useState('');

	const signatureOwnerId = signatureOwner?.userId ?? null;
	const profilePrimaryEmail = signatureOwner?.email;
	const emailOptions = getAvailableSignatureEmailOptions(userId);

	const availableEmails = useMemo(
		() => emailOptions.map((option) => option.email),
		[emailOptions],
	);
	const { data: signatureRecords = [], isFetching: isFetchingSignatures } =
		useGetUnlayerUserSignaturesQuery(
			{
				userId: signatureOwnerId,
				availableEmails,
			},
			{
				skip: !signatureOwnerId || availableEmails.length === 0,
			},
		);
	const [upsertSignature, { isLoading: isSavingSignature }] =
		useUpsertUnlayerUserSignatureMutation();
	const selectedSignatureRecord = useMemo(
		() =>
			signatureRecords.find(
				(record) => record.email === selectedEmailOption?.normalizedEmail,
			) ?? null,
		[selectedEmailOption?.normalizedEmail, signatureRecords],
	);
	const unlayerTagContext = useMemo(
		() =>
			buildUnlayerUserContext({
				selectedFrom: selectedEmailOption,
				userDetails: signatureOwner,
				fallbackEmail: profilePrimaryEmail,
			}),
		[profilePrimaryEmail, selectedEmailOption, signatureOwner],
	);
	const mergeTags = useMemo(
		() => getUnlayerTags(unlayerTagContext, null, null),
		[unlayerTagContext],
	);
	const tagData = useMemo(
		() => getUnlayerTagData({ user: unlayerTagContext, client: null, deal: null }),
		[unlayerTagContext],
	);

	useEffect(() => {
		if (hasInitialEmailSelectionRef.current || !profilePrimaryEmail) {
			return;
		}

		const defaultEmailOption = getDefaultProfileSignatureEmailOption({
			emailOptions,
			loggedInEmail: profilePrimaryEmail,
		});
		if (!defaultEmailOption) {
			return;
		}

		hasInitialEmailSelectionRef.current = true;
		setSelectedEmailOption(defaultEmailOption);
	}, [emailOptions, profilePrimaryEmail]);

	useEffect(() => {
		if (!selectedEmailOption) {
			return;
		}

		const emailStillAvailable = findAvailableSignatureEmailOption(
			emailOptions,
			selectedEmailOption.normalizedEmail,
		);
		if (emailStillAvailable) {
			if (emailStillAvailable.normalizedEmail !== selectedEmailOption.normalizedEmail) {
				setSelectedEmailOption(emailStillAvailable);
			}
			return;
		}

		setSelectedEmailOption(
			getDefaultProfileSignatureEmailOption({
				emailOptions,
				loggedInEmail: profilePrimaryEmail,
			}),
		);
	}, [emailOptions, profilePrimaryEmail, selectedEmailOption]);

	useEffect(() => {
		if (!selectedEmailOption) {
			return;
		}

		if (!selectedSignatureRecord) {
			setLoadError('');
			return;
		}

		if (selectedSignatureRecord.designJsonInvalid || !selectedSignatureRecord.designJson) {
			console.error('Saved Unlayer signature designJson is invalid', {
				email: selectedSignatureRecord.email,
				recordId: selectedSignatureRecord.id,
			});
			setLoadError(
				selectedSignatureRecord.designJsonError ||
					'Saved signature design could not be loaded.',
			);
			setDesignToLoad(getEmptyUnlayerDesign());
			return;
		}

		setLoadError('');
		setDesignToLoad(cloneDesign(selectedSignatureRecord.designJson));
	}, [selectedEmailOption, selectedSignatureRecord]);

	const handleEditorReady = useCallback((unlayer) => {
		editorRef.current = unlayer;
	}, []);

	const handleEditorChange = useCallback(() => {
		setSaveError('');
	}, []);

	const handleEmailChange = useCallback((nextEmailOption) => {
		setSelectedEmailOption(nextEmailOption ?? null);
		setSaveError('');
	}, []);

	const handleSave = useCallback(async () => {
		setSaveError('');
		if (!selectedEmailOption?.email) {
			setSaveError('Select an email before saving a signature.');
			return;
		}

		try {
			const liveSnapshot = await getUnlayerExport(editorRef.current);
			const resolvedHtml = resolveSignatureSnapshotHtml({
				html: liveSnapshot.html,
				tagData,
			});
			if (isSignatureHtmlEmpty(resolvedHtml)) {
				setSaveError('Signature content is required before saving.');
				return;
			}

			await upsertSignature({
				userId: signatureOwnerId,
				email: selectedEmailOption.email,
				designJson: liveSnapshot.design,
				html: resolvedHtml,
			}).unwrap();

			try {
				await logAuditActions({
					actionType: 'userUpdated',
					performedBy: {
						userId: userDetails?.userId,
						hubspotId: userDetails?.hubspotId,
						email: userDetails?.email ?? email,
						name: userDetails?.name,
					},
					targetUser: {
						userId: signatureOwner?.userId,
						hubspotId: signatureOwner?.hubspotId,
						email: signatureOwner?.email ?? selectedEmailOption.email,
						name: signatureOwner?.name,
					},
					changedFields: {
						unlayerSignature: selectedEmailOption.email,
					},
					previousValues: {
						unlayerSignature: selectedSignatureRecord?.email ?? null,
					},
				});
			} catch (auditError) {
				console.error('Failed to log audit action', auditError);
			}

			dispatch(
				showToast({
					message: 'Signature saved successfully.',
					success: true,
				}),
			);
		} catch (error) {
			console.error('Error saving Unlayer signature', error);
			setSaveError(error?.data?.message ?? 'Unable to save signature.');
			dispatch(
				showToast({
					message: 'Unable to save signature.',
					error: true,
				}),
			);
		}
	}, [
		dispatch,
		email,
		selectedEmailOption,
		selectedSignatureRecord,
		signatureOwner,
		signatureOwnerId,
		tagData,
		upsertSignature,
		userDetails,
	]);

	return {
		designToLoad,
		emailOptions,
		handleEditorChange,
		handleEditorReady,
		handleEmailChange,
		handleSave,
		isFetchingSignatures,
		isSavingSignature,
		loadError,
		mergeTags,
		saveError,
		selectedEmailOption,
	};
};
